import { I18n } from 'aws-amplify'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { requestCities } from '@/actions/operations/city'

import {
  getCurrentUser,
  getCurrentUserOrganizations,
  getOperationsCities,
  getOperationsCitiesCount,
  getOperationsLoading
} from '@/reducers/selectors'

import {
  Flex,
  Button,
  Cell,
  Text,
  Column,
  Row,
  Slot,
  Table,
  Loader
} from '@/primitives'

import HeaderV2 from '@/components/Operations/Shared/HeaderV2'

import { Pagination } from '@/elements'

import TableFilter from './TableFilter'

import history from '../../../../history'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_MANAGEMENT
} from '@/Util/PermissionUtils'

function CityTable({ state, dispatchState, modulePath, backPath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const organizations = getCurrentUserOrganizations()
  const cities = getOperationsCities()
  const citiesCount = getOperationsCitiesCount()
  const loading = getOperationsLoading()

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)
  const [showOrganization, setShowOrganization] = useState(false)
  const [fetchCalled, setFetchCalled] = useState(false)

  const fetchCities = useCallback(
    page => {
      const { organizations, countries, regions } = state
      if (
        organizations.length > 0 &&
        countries.length > 0 &&
        regions.length > 0
      ) {
        dispatch(
          requestCities({
            filter: {
              organizationId: organizations,
              countryId: countries,
              regionId: regions
            },
            limit: pageSize,
            offset: page - 1
          })
        )
        setFetchCalled(true)
      }
    },
    [state, pageSize, dispatch]
  )

  const changePage = useCallback(
    newPage => {
      dispatchState({ page: newPage })
      setFetchCalled(false)
    },
    [dispatchState]
  )

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_MANAGEMENT
        )
      )
    }
    if (organizations.length > 1) {
      setShowOrganization(true)
    }
  }, [currentUser, organizations])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.countries !== state.countries ||
      tableState.regions !== state.regions ||
      tableState.page !== state.page
    ) {
      if (!fetchCalled) {
        fetchCities(state.page)
      }
    }
  }, [fetchCities, state, tableState, fetchCalled])

  useEffect(() => {
    setTableState({ ...state })
  }, [state])

  const onCreate = () => history.push(`${modulePath}/create`)

  const onEdit = id => history.push(`${modulePath}/update/${id}`)

  const onAreas = (organizationId, countryId, regionId, id) => {
    dispatchState({
      state: {
        organizationId,
        countryId,
        regionId,
        cityId: id
      }
    })
    history.push(`${modulePath}/areas`)
  }

  const getOrganizationName = organizationId => {
    const organization = organizations.find(org => org.id === organizationId)
    return organization ? organization.name : null
  }

  return (
    <Fragment>
      <HeaderV2
        title={I18n.get('Manage Cities')}
        buttonText={I18n.get('Add City')}
        buttonCallback={onCreate}
        buttonIcon={'add'}
        showButton={canEdit}
        backPath={backPath}
      >
        <TableFilter state={state} dispatchState={dispatchState} />
      </HeaderV2>
      <Loader isLoading={loading}>
        {citiesCount === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{I18n.get('No cities.')}</Text>
          </Flex>
        )}
        {citiesCount > 0 && (
          <Table aria-colcount='2' className='Operations__Table'>
            <Slot name='head'>
              <Row>
                {showOrganization && (
                  <Column>{I18n.get('Organization')}</Column>
                )}
                <Column>{I18n.get('Country')}</Column>
                <Column>{I18n.get('Region')}</Column>
                <Column>{I18n.get('City')}</Column>
                <Column></Column>
              </Row>
            </Slot>
            <Slot name='body'>
              {cities.map(
                ({
                  id,
                  organizationId,
                  name,
                  countryId,
                  country,
                  regionId,
                  region
                }) => (
                  <Row key={id}>
                    {showOrganization && (
                      <Cell columnName={I18n.get('Organization')}>
                        {getOrganizationName(organizationId)}
                      </Cell>
                    )}
                    <Cell columnName={I18n.get('Country')}>{country.name}</Cell>
                    <Cell columnName={I18n.get('Region')}>{region.name}</Cell>
                    <Cell columnName={I18n.get('City')}>{name}</Cell>
                    <Cell className='Operations__Table__Column__Actions'>
                      {canEdit && (
                        <Button
                          variant='info'
                          size='small'
                          iconBefore='edit'
                          onClick={() => onEdit(id)}
                        />
                      )}
                      <Button
                        variant='info'
                        size='small'
                        iconBefore='edit_note'
                        onClick={() =>
                          onAreas(organizationId, countryId, regionId, id)
                        }
                      >
                        <Text fontWeight={700} textTransform='uppercase'>
                          {I18n.get('Areas')}
                        </Text>
                      </Button>
                    </Cell>
                  </Row>
                )
              )}
            </Slot>
          </Table>
        )}
        {citiesCount > pageSize && (
          <Pagination
            type='simple'
            totalItems={citiesCount}
            totalPages={totalPages}
            setTotalPages={setTotalPages}
            page={tableState.page}
            setPage={changePage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      </Loader>
    </Fragment>
  )
}

export default CityTable
